import Card from '@components/UI/Card/Card'
import NewsItemReaction from '../components/News/NewItemReaction/NewsItemReaction'
import NewsItemContent from '../components/News/NewsItemContent/NewsItemContent'
import NewsItemHeader from '../components/News/NewsItemHeader/NewsItemHeader'
import { News, NewsItemProps, NewsReactionType } from '@models/news'
import { useIntl } from 'react-intl'
import NewsItemCtaContainer from '../components/News/NewsItemCtaContainer/NewsItemCtaContainer'
import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { AppState } from '@redux/states/appState'
import { copyLinkToClipboard, reactionToNews, setNewsDetails } from '@redux/actions/news'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper'
import {
    Analytics,
    commentsActions,
    CreateMessageBody,
    GetMessageListActionParams,
    GetMessageParams,
    layoutActions,
    ModalsType,
    UploadFileType,
} from '@Techedge/digitalportal-shared'
import { EmojiPicker } from '@Techedge/smart-shared'
import { LuxCommentList, LuxAddComment, LuxIconSpinner } from '@lux/wardrobe-lib'
import { ROOT_PAGE_NUM } from '../redux/reducers/news'
import { localStorageBreadcrumbKey } from '@utils/export-utils'

const NewsItem = (props: NewsItemProps) => {
    const {
        news,
        user,
        reactions,
        isDetailsMode,
        topicMap,
        loadingReactions,
        setNewsDetails,
        openActivity,
        createMessage,
        editMessage,
        deleteMessage,
        uploadFile,
        loadMore,
        getMessageList,
        reactionMessage,
        initTopic,
        resetTopic,
        setShowSidebar,
        reactToNews,
        removeFile,
        copyLinkToClipboard,
    } = props

    const intl = useIntl()

    const newsItemClass = 'home-news-item'

    const [showComments, setShowComments] = useState(isDetailsMode)
    const [showAllText, setShowAllText] = useState(isDetailsMode)

    useEffect(() => {
        if (!!news.id && showComments) {
            initTopic(news.id)
        }
        return () => {
            resetTopic(news.id)
        }
    }, [news.id, showComments])

    useEffect(() => {
        if (!!topicMap[news.id] && showComments) {
            getMessageList({
                projectId: news.id.toString(),
                isRootLoading: topicMap[news.id]?.pageNumMap[ROOT_PAGE_NUM] === 1,
            })
        }
    }, [topicMap[news.id]?.pageNumMap[ROOT_PAGE_NUM]])

    useEffect(() => {
        setShowSidebar(!isDetailsMode)
    }, [isDetailsMode])

    const onCommentsHandler = () => {
        if (window.innerWidth > 767 || isDetailsMode) {
            setShowComments((value) => !value)
            return
        }
        if (!isDetailsMode) {
            Analytics._sendAnalyticsLinkEvent('Home', 'Post:Detail', 'Static')
            setNewsDetails(news)
        }
    }

    const editLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.edit',
                defaultMessage: 'Edit',
            }),
        [intl]
    )
    const deleteLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.delete',
                defaultMessage: 'Delete',
            }),
        [intl]
    )
    const loadMoreLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.load.more',
                defaultMessage: 'Load more',
            }),
        [intl]
    )
    const loadMoreReplyLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.load.more.reply',
                defaultMessage: 'Load more reply',
            }),
        [intl]
    )
    const replyLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.reply',
                defaultMessage: 'Reply',
            }),
        [intl]
    )
    const addAttachmentLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.add.attachment',
                defaultMessage: 'Add attachment',
            }),
        [intl]
    )
    const animatedGifLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.animated.gif',
                defaultMessage: 'Animated GIF',
            }),
        [intl]
    )
    const emojiLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.emoji',
                defaultMessage: 'Emoji',
            }),
        [intl]
    )
    const typeHereLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.type.here',
                defaultMessage: 'Type here...',
            }),
        [intl]
    )

    const readMoreHandler = () => {
        setShowAllText((value) => !value)
    }

    const backHandler = () => {
        setNewsDetails(null)
    }

    const onSubmit = (e) => {
        createMessage(
            {
                text: e.value,
                isGif: e.isGif || false,
                reference: {
                    projectId: news.id.toString(),
                    parentId: e.id,
                },
            },
            news.id,
            e.nodeIds || []
        )
    }

    const onEditMessage = (e) => {
        editMessage(
            {
                text: e.editedComment,
                isGif: false,
                reference: {
                    projectId: news.id.toString(),
                },
            },
            e.id,
            e.nodeIds,
            e.parentCommentId
        )
    }

    const onDeleteMessage = (e) => {
        deleteMessage(
            e.id,
            { projectId: news.id.toString(), parentId: e.parentCommentId },
            e.nodeIds,
            e.parentCommentId
        )
    }

    const onUploadFile = (e) => {
        uploadFile(e.file || e, UploadFileType.FILE, e.id, news.id.toString(), e.nodeIds)
    }

    const onLoadMore = (messageId?: string, nodeIds?: string[]) => {
        loadMore(messageId, { projectId: news.id.toString() }, news.id.toString(), nodeIds)
    }

    const onUpdateTotalReactions = (e) => {
        reactionMessage(
            {
                reference: {
                    projectId: news.id.toString(),
                },
            },
            e.id,
            news.id.toString(),
            e.nodeIds,
            e.parentCommentId,
            e.type
        )
    }

    const onGif = (e) => {
        onSubmit({
            value: e.file?.images?.original.url || e.images?.original.url,
            isGif: true,
            id: e.file && e.id,
            nodeIds: e.nodeIds,
        })
    }

    const onRemoveFile = (e) => {
        removeFile(e.file?.id || e.id, !!e.file?.id ? e.id : null, news.id, e.nodeIds)
    }

    return (
        <Card hideBorderOnMobile={true} class={isDetailsMode ? 'no-padding' : ''}>
            <div className={newsItemClass}>
                <NewsItemHeader
                    onBack={backHandler}
                    openActivity={() => openActivity(news)}
                    isDetailsMode={isDetailsMode}
                    userName={news.author.name}
                    userLastname={news.author.lastname}
                    userNickname={news.author.alias}
                    avatarImage={news.author.avatar}
                    creationDate={news.creationDate}
                    type={news.type}
                    typeKey={intl.formatMessage({
                        id: `home.news.type.${news.typeKey}`,
                        defaultMessage: '',
                    })}
                />
                <NewsItemContent
                    onReadMore={readMoreHandler}
                    openActivity={() => openActivity(news)}
                    showAllContent={showAllText}
                    title={news.title}
                    content={news.content}
                    hashtagList={news.hashtagList}
                />
                {news.imageList.length === 1 && (
                    <div
                        className={`${newsItemClass}__image-container ${
                            isDetailsMode ? `${newsItemClass}__image-container--full` : ''
                        }`}>
                        <img
                            src={news.imageList[0]}
                            onClick={() => openActivity(news)}
                            className={`${newsItemClass}__image`}
                        />
                    </div>
                )}
                {news.imageList.length > 1 && (
                    <Swiper slidesPerView={1} modules={[Navigation]} navigation={true}>
                        {news.imageList.map((image, index) => (
                            <SwiperSlide key={index}>
                                <div
                                    className={`${newsItemClass}__image-container ${
                                        isDetailsMode
                                            ? `${newsItemClass}__image-container--full`
                                            : ''
                                    }`}>
                                    <img
                                        src={image}
                                        onClick={() => openActivity(news)}
                                        className={`${newsItemClass}__image`}
                                    />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                )}
                {news.enableCommentsAndReactions &&
                    ((!!reactions && reactions.count > 0) || news.totalComments > 0) && (
                        <NewsItemReaction
                            totalComments={news.totalComments}
                            reactions={reactions}
                            loadingReactions={loadingReactions[news.id]}
                        />
                    )}
                <NewsItemCtaContainer
                    id={news.id}
                    enableCommentsAndReactions={news.enableCommentsAndReactions}
                    onComment={onCommentsHandler}
                    showComments={showComments}
                    updateTotalReactions={(id: string, reaction: NewsReactionType) =>
                        reactToNews(id, reaction)
                    }
                    isCommentListLoading={topicMap[news.id]?.loading}
                    onCopyLink={() => {
                        navigator.clipboard.writeText(
                            `${window.location.origin}/activity?projectId=${news.id}&isShared=true`
                        )
                        copyLinkToClipboard(
                            intl.formatMessage({
                                id: 'home.link_copied_successfully',
                                defaultMessage: 'Link copied successfully!',
                            })
                        )
                    }}
                />
                {news.enableCommentsAndReactions && showComments && (
                    <>
                        <div className="home-comment-list-container">
                            {topicMap[news.id]?.loading && <LuxIconSpinner classes="mb-2" />}
                            {!topicMap[news.id]?.loading && (
                                <LuxCommentList
                                    emojiPicker={EmojiPicker}
                                    creatorInfo={{
                                        userName: user?.given_name,
                                        userLastname: user?.family_name,
                                        avatarImage: user?.profilePicURL,
                                    }}
                                    commentsList={topicMap[news.id]?.messageList || []}
                                    enableLoadMore={
                                        topicMap[news.id]?.messageList.length <
                                        topicMap[news.id]?.totalCount
                                    }
                                    isLoadMoreLoading={topicMap[news.id]?.loadingMore}
                                    replyLabel={replyLabel}
                                    bgWhite
                                    loadMoreLabel={loadMoreLabel}
                                    loadMoreReplyLabel={loadMoreReplyLabel}
                                    editLabel={editLabel}
                                    deleteLabel={deleteLabel}
                                    addPlaceholder={typeHereLabel}
                                    addLinkLabel={addAttachmentLabel}
                                    addGifLabel={animatedGifLabel}
                                    addEmojiLabel={emojiLabel}
                                    giphyKey={process.env.GIPHY_KEY}
                                    onEdit={onEditMessage}
                                    onDelete={onDeleteMessage}
                                    onRemoveFile={onRemoveFile}
                                    onUpload={onUploadFile}
                                    onSubmit={onSubmit}
                                    onGif={onGif}
                                    onLoadMore={onLoadMore}
                                    updateTotalReactions={onUpdateTotalReactions}
                                />
                            )}
                        </div>
                        <LuxAddComment
                            emojiPicker={EmojiPicker}
                            userName={user?.given_name}
                            userLastname={user?.family_name}
                            userNickname={user?.nickname}
                            avatarImage={user?.profilePicURL}
                            placeholder={typeHereLabel}
                            addLinkLabel={addAttachmentLabel}
                            addGifLabel={animatedGifLabel}
                            addEmojiLabel={emojiLabel}
                            attachmentList={topicMap[news.id]?.attachmentMap[ROOT_PAGE_NUM]}
                            isLoading={topicMap[news.id]?.loadingMessage}
                            giphyKey={process.env.GIPHY_KEY}
                            onRemoveFile={onRemoveFile}
                            onUpload={onUploadFile}
                            onSubmit={onSubmit}
                            onGif={onGif}
                        />
                    </>
                )}
            </div>
        </Card>
    )
}

const mapStateToProps = (state: AppState) => ({
    topicMap: state.commentsReducer.topicMap,
    user: state.userReducer.user,
    loadingReactions: state.newsReducer.loadingReactions,
})

const mapDispatchToProps = (dispatch: any) => ({
    setNewsDetails: (newsDetails: News) => dispatch(setNewsDetails(newsDetails)),
    openActivity: (a) => {
        localStorage.setItem(
            localStorageBreadcrumbKey,
            window.location.href
        )
        dispatch(
            layoutActions.setModals({
                type: ModalsType.TASK_EXECUTION,
                props: { id: a.id, storeIds: a.store_ids },
            })
        )
    },
    createMessage: (body: CreateMessageBody, topicId: string, nodeIds: string[]) =>
        dispatch(commentsActions.createMessage(body, topicId, nodeIds)),
    getMessageList: (args: GetMessageListActionParams) =>
        dispatch(commentsActions.getMessageList(args)),
    editMessage: (body: CreateMessageBody, id: string, nodeIds: string[], parentId: string) =>
        dispatch(commentsActions.editMessage(body, id, nodeIds, parentId)),
    uploadFile: (
        file,
        type: UploadFileType,
        parentId: string,
        topicId: string,
        nodeIds: string[]
    ) => dispatch(commentsActions.uploadFile(file, type, parentId, topicId, nodeIds)),
    removeFile: (fileId: string, parentId: string, topicId: string, nodeIds: string[]) =>
        dispatch(commentsActions.removeFile(fileId, parentId, topicId, nodeIds)),
    deleteMessage: (id: string, body: GetMessageParams, nodeIds: string[], parentId: string) =>
        dispatch(commentsActions.deleteMessage(id, body, nodeIds, parentId)),
    loadMore: (
        messageId: string,
        partialBody: GetMessageListActionParams,
        topicId: string,
        nodeIds: string[]
    ) => dispatch(commentsActions.loadMore(messageId, partialBody, topicId, nodeIds)),
    initTopic: (topicId: string) => dispatch(commentsActions.initTopic(topicId)),
    resetTopic: (topicId: string) => {
        dispatch(commentsActions.setTotalCount(0, topicId))
        dispatch(commentsActions.deleteTopic(topicId))
    },
    setShowSidebar: (show: boolean) => dispatch(layoutActions.setShowSidebar(show)),
    reactToNews: (id: string, reaction: NewsReactionType) => dispatch(reactionToNews(id, reaction)),
    reactionMessage: (
        body: CreateMessageBody,
        id: string,
        topicId: string,
        nodeIds: string[],
        parentId: string,
        reaction: NewsReactionType
    ) => dispatch(commentsActions.reactionMessage(body, id, topicId, nodeIds, parentId, reaction)),
    copyLinkToClipboard: (message: string) => dispatch(copyLinkToClipboard(message)),
})

export default connect(mapStateToProps, mapDispatchToProps)(NewsItem)
